import { ForwardRefRenderFunction, FunctionComponent, ReactNode, forwardRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { t } from '@/utils/i18n';
import { useFocus } from '@/components/common/focus';
import { CaretLeft } from '@/components/common/icons/Caret';
import { Close } from '@/components/common/icons/Close';
import { Info } from '@/components/common/icons/Info';
import './panel.scss';

export const PanelContent: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <div className="panel__content">{children}</div>
);

interface PanelHeaderProps {
  children: ReactNode;
  label: string;
  onClose?: () => void;
  onBack?: () => void;
}

export const PanelHeader: FunctionComponent<PanelHeaderProps> = ({
  label,
  onClose,
  onBack,
  children,
}) => (
  <div className="panel__header">
    <h3 className="panel__header__headline ellipsis">{children}</h3>
    {onClose && (
      <button type="button" title={label} className="panel__header__close" onClick={onClose}>
        <Close className="panel__header__close-icon" />
      </button>
    )}
    {onBack && (
      <button type="button" title={label} className="panel__header__back" onClick={onBack}>
        <CaretLeft className="panel__header__back-icon" />
      </button>
    )}
  </div>
);

interface PanelProps {
  className?: string;
  hidden?: boolean; // element is hidden by overlay
  active?: boolean; // element is mounted to the dom
  onEntered?: () => void;
  children: ReactNode;
}

const PanelWithRef: ForwardRefRenderFunction<HTMLDivElement, PanelProps> = (
  { className, active = true, hidden, onEntered, children },
  ref
) => (
  <CSSTransition in={active} appear mountOnEnter unmountOnExit onEntered={onEntered} timeout={200}>
    <div className={cn(className, 'panel', { 'panel--hidden': hidden })} ref={ref} tabIndex={-1}>
      {children}
    </div>
  </CSSTransition>
);

export const Panel = forwardRef<HTMLDivElement, PanelProps>(PanelWithRef);

interface SubPanelProps {
  children: ReactNode;
  active: boolean;
  headline: string;
  parent: string;
  className?: string;
  onBack: () => void;
}

export const SubPanel: FunctionComponent<SubPanelProps> = ({
  active,
  headline,
  parent,
  className,
  onBack,
  children,
}) => {
  const [panel, onEntered] = useFocus(active);
  return (
    <CSSTransition
      in={active}
      appear
      mountOnEnter
      unmountOnExit
      onEntered={onEntered}
      timeout={200}
    >
      <div className={cn(className, 'sub-panel')} ref={panel} tabIndex={-1}>
        <PanelHeader label={t('modal.back', parent)} onBack={onBack}>
          {headline}
        </PanelHeader>
        <PanelContent>{children}</PanelContent>
      </div>
    </CSSTransition>
  );
};

export const PanelInfo: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <div className="panel__info">
    <Info className="panel__info__icon" />
    <div>{children}</div>
  </div>
);
